<template>
  <section class="stats-section" ref="sectionRef">
    <div class="stats-container">
      <div
        v-for="(stat, index) in stats"
        :key="stat.key"
        class="stat-item"
        data-reveal="up"
        :data-reveal-delay="index + 1"
      >
        <div class="stat-number">
          <span v-if="stat.prefix" class="stat-prefix">{{ stat.prefix }}</span>
          <span class="stat-value">{{ isVisible ? displayValues[index] : 0 }}</span>
          <span v-if="stat.suffix" class="stat-suffix">{{ stat.suffix }}</span>
        </div>
        <div class="stat-label">{{ stat.label || $t(`landing.stats_${stat.key}`) }}</div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const props = defineProps({
  items: { type: Array, default: null }
})

const sectionRef = ref<HTMLElement | null>(null)
const isVisible = ref(false)

// NOTE: the live artist/label/release counts moved to LandingTrust (real
// cross-tenant data). Keeping a hardcoded "500+ artists" here contradicted
// that section's "1.8K+", so this strip now carries complementary claims
// the Trust wall doesn't — platforms, speed, royalty share.
const defaultStats = [
    { key: 'platforms', target: 30, suffix: '+', prefix: '', label: '' },
    { key: 'delivery', target: 24, suffix: 'h', prefix: '', label: '' },
    { key: 'royalties', target: 100, suffix: '%', prefix: '', label: '' },
]

const stats = computed(() => {
  if (props.items && props.items.length > 0) {
    return props.items.map((item: any, i: number) => ({
      key: `custom-${i}`,
      target: item.value || 0,
      suffix: item.suffix || '',
      prefix: item.prefix || '',
      label: item.label || '',
    }))
  }
  return defaultStats
})

const displayValues = reactive(stats.value.map(() => 0))

const animateCounters = () => {
    const duration = 2000
    const start = performance.now()

    const step = (now: number) => {
        const progress = Math.min((now - start) / duration, 1)
        const eased = 1 - Math.pow(1 - progress, 3) // ease-out cubic

        stats.value.forEach((stat: any, i: number) => {
            displayValues[i] = Math.round(eased * stat.target)
        })

        if (progress < 1) {
            requestAnimationFrame(step)
        }
    }

    requestAnimationFrame(step)
}

onMounted(() => {
    if (!sectionRef.value) return

    const observer = new IntersectionObserver(
        (entries) => {
            if (entries[0]?.isIntersecting) {
                isVisible.value = true
                animateCounters()
                observer.disconnect()
            }
        },
        { threshold: 0.2 }
    )

    observer.observe(sectionRef.value)

    // Fallback: if already in view (SSR hydration), trigger after short delay
    setTimeout(() => {
        if (!isVisible.value && sectionRef.value) {
            const rect = sectionRef.value.getBoundingClientRect()
            if (rect.top < window.innerHeight && rect.bottom > 0) {
                isVisible.value = true
                animateCounters()
                observer.disconnect()
            }
        }
    }, 500)

    onBeforeUnmount(() => observer.disconnect())
})
</script>

<style scoped>
/* Mobile-first. Ladder: 480 / 640 / 768 / 1024 / 1280 (variables.css). */
.stats-section {
  position: relative;
  padding: clamp(48px, 6vw, 80px) 0;
  background:
    linear-gradient(to right, rgb(var(--primary-channels) / 0.03), rgb(var(--secondary-channels) / 0.03)),
    var(--bg-color);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
}

/* auto-fit: works for the default 3 stats and for builder pages with any
   other count — no orphan cells on phones. */
.stats-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 var(--space-6);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: clamp(20px, 4vw, 32px);
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-family: var(--font-heading);
  font-size: clamp(2rem, 1.4rem + 2.8vw, 3.5rem);
  font-weight: 800;
  line-height: 1;
  margin-bottom: 8px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.stat-prefix,
.stat-suffix {
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.stat-value {
  font-variant-numeric: tabular-nums;
}

.stat-label {
  font-size: clamp(0.78rem, 0.72rem + 0.3vw, 0.9rem);
  color: var(--text-muted);
  font-weight: 500;
}
</style>
